import { getRandomNumberFromString } from '../../../../../utils/miscUtil';
import { MockService } from '../../../../../types/rest/mockServiceTypes';
import _ from 'lodash';

export type DeploymentStageStatus =
  | 'STARTING'
  | 'GENERATING_DATA'
  | 'STARTING_CONTAINER'
  | 'WAITING_FOR_DOMAIN'
  | 'CONTAINER_READY'
  | 'INDEXING_DATA'
  | 'FINISHED'
  | string;

/**
 * Dictionary of booting messages.
 */
const bootingMessages = {
  STARTING_CONTAINER: [
    'Bringing the container to life… almost there!',
    'Brewing your container… almost ready to serve!',
    'Getting your container ship on course… full steam ahead!',
    'Building your digital masterpiece… hold tight!',
    'The wait is almost over, stay tuned!',
  ],
  CONTAINER_READY: [
    'Almost there, hang in there!',
    'Just polishing the final details…',
    'Just a few more moments of suspense…',
    'Fetching the final piece of the puzzle!',
  ],
  FINISHED: [
    'All set! Your virtual service is ready to roll!',
    "You're good to go! Everything is up and running!",
    'Success! Your container is prepped and ready!',
    'Done and dusted! Ready when you are!',
    'We’re live! Your virtual service is ready for action!',
  ],
  INDEXING_DATA: [
    'Indexing your data… putting everything in order',
    'Your data is being indexed… precision in progress',
    'Indexing underway… data organization is key',
    'Indexing in progress… getting your data shipshape',
  ],
};

/**
 * Gets pseudo randomly a message from the list of messages based on updated time stamp.
 * @param status
 * @param mockService
 */
export const getRandomMessageByStatus = (status: DeploymentStageStatus, mockService: MockService) => {
  const messages = bootingMessages[status];
  if (_.isNil(messages)) {
    return '';
  }
  const randIndex = getRandomNumberFromString(mockService.name, _.size(messages) - 1);
  return bootingMessages[status][randIndex];
};

/**
 * Gets fancy title based on current container status.
 *
 * @param status current status of the deployment
 * @param mockService
 */
export const getBootingHeaderText = (
  status: DeploymentStageStatus,
  mockService: MockService,
): {
  title: string;
  text: string;
} => {
  switch (status) {
    case 'STARTING':
      return {
        title: 'Initializing',
        text: 'Starting your Virtual Service might take about 1-2 minutes',
      };
    case 'GENERATING_DATA':
      return {
        title: 'Generating Data',
        text: 'Preparing service data based on your data settings',
      };
    case 'WAITING_FOR_DOMAIN':
      return {
        title: 'Waiting for Domain',
        text: 'Obtaining a unique URL for your virtual service.',
      };
    case 'STARTING_CONTAINER':
      return {
        title: 'Starting Container',
        text: getRandomMessageByStatus('STARTING_CONTAINER', mockService),
      };
    case 'CONTAINER_READY':
      return { title: 'Container Started', text: getRandomMessageByStatus('CONTAINER_READY', mockService) };
    case 'INDEXING_DATA':
      return { title: 'Indexing data', text: getRandomMessageByStatus('INDEXING_DATA', mockService) };
    case 'FINISHED':
      return { title: 'Ready', text: getRandomMessageByStatus('FINISHED', mockService) };
    default:
      return {
        title: '',
        text: '',
      };
  }
};
